import { categorizeTabUrl, hostFromTabUrl, type TabCategory } from './tabCategories';

export interface TabPatternView {
  id: string;
  name: string;
  frequency: number;
  category: string;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  lastDetected: Date;
  description: string;
}

export interface TabPatternSource {
  url: string;
  title?: string | null;
  updated_at?: string;
}

export function parsePatternFrequency(raw: string): number {
  const match = raw.match(/(\d+)/);
  if (!match) return 50;
  return Math.min(100, parseInt(match[1], 10) * 10);
}

export function derivePatternsFromTabs(tabs: TabPatternSource[]): TabPatternView[] {
  if (tabs.length === 0) return [];

  const patterns: TabPatternView[] = [];
  const now = new Date();
  const categoryCounts = new Map<TabCategory, number>();
  const domainCounts = new Map<string, number>();
  let newestUpdate = '';

  for (const tab of tabs) {
    const category = categorizeTabUrl(tab.url);
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
    const host = hostFromTabUrl(tab.url);
    if (host) {
      domainCounts.set(host, (domainCounts.get(host) ?? 0) + 1);
    }
    if (tab.updated_at && tab.updated_at > newestUpdate) {
      newestUpdate = tab.updated_at;
    }
  }

  const total = tabs.length;
  const lastDetected = newestUpdate ? new Date(newestUpdate) : now;

  let topCategory: TabCategory = 'General';
  let topCount = 0;
  for (const [category, count] of categoryCounts) {
    if (count > topCount) {
      topCategory = category;
      topCount = count;
    }
  }

  if (topCount >= 2) {
    const share = Math.round((topCount / total) * 100);
    patterns.push({
      id: `category-${topCategory}`,
      name: `${topCategory} focus`,
      frequency: share,
      category: topCategory,
      confidence: Math.min(95, 55 + topCount * 8),
      trend: topCount >= 3 ? 'up' : 'stable',
      lastDetected,
      description: `${topCount} of ${total} open tabs are ${topCategory.toLowerCase()} sites.`,
    });
  }

  for (const [host, count] of domainCounts) {
    if (count < 2) continue;
    const share = Math.round((count / total) * 100);
    patterns.push({
      id: `domain-${host}`,
      name: `${host} cluster`,
      frequency: share,
      category: categorizeTabUrl(`https://${host}`),
      confidence: Math.min(92, 50 + count * 12),
      trend: count >= 3 ? 'up' : 'stable',
      lastDetected,
      description: `${count} tabs open on ${host}. Consider grouping them.`,
    });
  }

  const activeCategories = [...categoryCounts.values()].filter((count) => count > 0).length;
  if (activeCategories >= 3) {
    patterns.push({
      id: 'cross-category',
      name: 'Multi-topic session',
      frequency: Math.min(100, activeCategories * 15),
      category: 'Workflow',
      confidence: Math.min(88, 45 + activeCategories * 10),
      trend: 'stable',
      lastDetected,
      description: `Browsing spans ${activeCategories} categories across ${total} tabs.`,
    });
  }

  if (total >= 5) {
    patterns.push({
      id: 'high-volume',
      name: 'High tab volume',
      frequency: Math.min(100, total * 8),
      category: 'Productivity',
      confidence: Math.min(85, 40 + total * 4),
      trend: total >= 10 ? 'up' : 'stable',
      lastDetected,
      description: `${total} active tabs in this session.`,
    });
  }

  patterns.sort((a, b) => b.confidence - a.confidence);

  const seen = new Set<string>();
  const unique: TabPatternView[] = [];
  for (const pattern of patterns) {
    if (seen.has(pattern.id)) continue;
    seen.add(pattern.id);
    unique.push(pattern);
  }
  return unique.slice(0, 12);
}

export function mergeTabPatterns(
  apiPatterns: TabPatternView[],
  derivedPatterns: TabPatternView[],
): TabPatternView[] {
  const merged = [...apiPatterns];
  const seen = new Set(apiPatterns.map((row) => row.id.toLowerCase()));
  for (const pattern of derivedPatterns) {
    const key = pattern.id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(pattern);
  }
  merged.sort((a, b) => b.confidence - a.confidence);
  return merged;
}

export function patternsThisWeek(patterns: TabPatternView[]): number {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return patterns.filter((pattern) => pattern.lastDetected.getTime() >= weekAgo).length;
}
